import React, { useCallback, useState } from 'react';
import { Button, Input, Modal, Select, Spin, Tooltip, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { createProperty } from '../../../redux/api/documents/propertiesThunks';
import { selectPropertyTypes } from '../../../redux/api/documents/documentsSelectors';
import { PropertyType } from '../../../types';

export interface AddPropertyActionProps {
    documentId: string;
    defaultPropertyTypeId?: string;
}

const AddPropertyAction: React.FC<AddPropertyActionProps> = ({ documentId, defaultPropertyTypeId }) => {
    const dispatch = useAppDispatch();
    const propertyTypes = useAppSelector(selectPropertyTypes);

    const [visible, setVisible] = useState(false);
    const [propertyTypeId, setPropertyTypeId] = useState<string | undefined>(defaultPropertyTypeId);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(false);

    const handleOpen = useCallback(() => {
        setPropertyTypeId(defaultPropertyTypeId);
        setText('');
        setVisible(true);
    }, [defaultPropertyTypeId]);

    const handleCancel = useCallback(() => {
        setVisible(false);
    }, []);

    const handleSave = useCallback(async () => {
        if (!propertyTypeId || !text.trim()) return;
        setLoading(true);
        try {
            await dispatch(createProperty({ documentId, propertyTypeId, text: text.trim() })).unwrap();
            notification.success({ message: 'Свойство добавлено', description: 'ML-артефакты рассчитываются в фоне.' });
            setVisible(false);
        } catch (error: any) {
            notification.error({
                message: 'Ошибка при создании свойства',
                description: error.message || 'Не удалось создать свойство.',
            });
        } finally {
            setLoading(false);
        }
    }, [dispatch, documentId, propertyTypeId, text]);

    return (
        <>
            <Tooltip title="Добавить свойство">
                <Button icon={<PlusOutlined />} size="small" onClick={handleOpen} />
            </Tooltip>
            <Modal
                title="Новое свойство"
                open={visible}
                onOk={handleSave}
                onCancel={handleCancel}
                okText="Создать"
                cancelText="Отмена"
                okButtonProps={{ disabled: !propertyTypeId || !text.trim(), loading }}
            >
                <Spin spinning={loading} tip="Создание свойства...">
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ marginBottom: 4, fontWeight: 500 }}>Тип свойства</div>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Выберите тип свойства"
                            value={propertyTypeId}
                            onChange={(v) => setPropertyTypeId(v)}
                        >
                            {propertyTypes.map((pt: PropertyType) => (
                                <Select.Option key={pt.id} value={pt.id}>
                                    {pt.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <div style={{ marginBottom: 4, fontWeight: 500 }}>Текст свойства</div>
                        <Input.TextArea
                            rows={4}
                            placeholder="Введите текст свойства..."
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                        />
                    </div>
                </Spin>
            </Modal>
        </>
    );
};

export default React.memo(AddPropertyAction);
